import React, { useState, useCallback, useRef, useEffect } from 'react';
import { addVitals } from '../../services/api/visitMetadataApi';
import useOutsideClose from '../../hooks/useOutsideClose';
import useAnchoredRect, { clampToViewport } from '../../hooks/useAnchoredRect';
import {
  VITAL_DEFINITIONS,
  DEFAULT_VITAL_KEYS,
  OPTIONAL_VITAL_KEYS,
} from '../../constants/visitMetadata';
import './AddVitals.css';

/**
 * AddVitals — Vitals entry form with Indian clinical units.
 *
 * Default vitals: Body Temperature, BP Systolic/Diastolic, Heart Rate
 * Optional: Respiratory Rate, SpO2, Weight, Height
 * Custom vitals can be added manually.
 *
 * Validations:
 *  - Range checking with visual warnings (yellow for out-of-range)
 *  - Step-based input (integer vs decimal)
 *  - Unit display next to each field
 *
 * Layout (bk-993, variant A): the vital grid is driven by a CSS container
 * query on the form's own inline size (AddVitals.css), not the viewport —
 * 1 column below 460px, 2 above, 3 at >=900px — so it reacts to the width
 * the Visit Actions accordion actually gives it. See AddVitals.css for the
 * full writeup of the overflow bug this replaces.
 */

// Ratified copy — PO draft, stories.md "Nurse vitals" (bk-993 / S21),
// RE-RATIFIED after Business R1: `VITAL_DEFINITIONS.min/max` are DATA-ENTRY
// PLAUSIBILITY bounds (what the form accepts before it queries a typo), NOT
// a clinical normal range — the word "normal" is banned on this screen.
// `VITALS_NORMAL_RANGE` is RETIRED; do not reintroduce it or add real
// clinical normal ranges to VITAL_DEFINITIONS.
//
// RV-1 (Business re-verdict) — no `title=` tooltip on the visible range line:
// a dev-written string naming `VITAL_DEFINITIONS` is a code identifier, not
// nurse-facing copy (no dev notes in UI copy). The visible `Accepts X–Y U`
// text already carries the meaning; if a tooltip is ever wanted here, PO
// writes and ratifies the exact words first.
function acceptedRangeLabel(defn) {
  const { min, max, unit } = defn;
  return unit ? `Accepts ${min}–${max} ${unit}` : `Accepts ${min}–${max}`;
}
const VITALS_NO_RANGE = 'No range set';

export default function AddVitals({ visitId, onSuccess, onClose }) {
  // Active vital keys (default + user-added)
  const [activeKeys, setActiveKeys] = useState([...DEFAULT_VITAL_KEYS]);
  // Values map: { body_temperature: '98.6', ... }
  const [values, setValues] = useState({});
  // Custom vitals: [{ key, label, value, unit }]
  const [customVitals, setCustomVitals] = useState([]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // RV-5 (owner click-through) — the form clears its inputs back to
  // placeholders after Save with nothing to show where the nurse is
  // looking. Holds the EXACT payload just written (write-time invariant —
  // not an eventually-consistent read-back) so a "Saved" summary can
  // render right at the form immediately after success.
  const [savedSummary, setSavedSummary] = useState(null);

  const addBtnRef = useRef(null);
  const menuRef = useRef(null);
  const customFormRef = useRef(null);
  const customNameRef = useRef(null);

  // RV-4 (owner click-through) — the picker used to be `position:absolute`
  // under `.av-add-wrap`, so an ancestor's `overflow:hidden`/`auto` (the
  // Visit Actions accordion, in every mount) clipped its tail — the
  // custom-vital row was the last thing in it, so it was the one nobody
  // could reach. Rect-anchored `position:fixed` (same escape the pad's
  // medicine box / lab-list picker already use — LabListPicker.jsx) means
  // a fixed box's containing block is the viewport: no ancestor can clip
  // or shift it, in the accordion, on NurseTaskCards, or on the
  // receptionist dashboard card. Custom Vital also moves to the TOP of the
  // list so it never again depends on the tail being visible.
  const anchor = useAnchoredRect(showAddMenu, addBtnRef);
  const placedMenu = anchor
    ? clampToViewport(anchor, { width: anchor.width, height: menuRef.current?.getBoundingClientRect().height || 0 })
    : null;

  // Available optional vitals (not yet active)
  const availableOptionals = OPTIONAL_VITAL_KEYS.filter(
    (k) => !activeKeys.includes(k)
  );

  const closeAddMenu = useCallback(() => setShowAddMenu(false), []);
  useOutsideClose(showAddMenu, [menuRef, addBtnRef], closeAddMenu);

  // Esc closes the picker (useOutsideClose only covers click-away).
  useEffect(() => {
    if (!showAddMenu) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeAddMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showAddMenu, closeAddMenu]);

  // Custom-vital form stays anchored under its trigger — scroll it into
  // view and focus the name field the moment it opens.
  useEffect(() => {
    if (showCustomForm) {
      customFormRef.current?.scrollIntoView({ block: 'nearest' });
      customNameRef.current?.focus();
    }
  }, [showCustomForm]);

  const handleAddVital = useCallback((key) => {
    setActiveKeys((prev) => [...prev, key]);
    setShowAddMenu(false);
  }, []);

  const handleRemoveVital = useCallback((key) => {
    setActiveKeys((prev) => prev.filter((k) => k !== key));
    setValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleAddCustom = useCallback(() => {
    if (!customLabel.trim()) return;
    const key = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setCustomVitals((prev) => [
      ...prev,
      { key, label: customLabel.trim(), value: '', unit: customUnit.trim() || '' },
    ]);
    setCustomLabel('');
    setCustomUnit('');
    setShowCustomForm(false);
  }, [customLabel, customUnit]);

  const handleRemoveCustom = useCallback((key) => {
    setCustomVitals((prev) => prev.filter((v) => v.key !== key));
  }, []);

  const handleValueChange = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // A new edit starting makes the last "Saved" summary stale.
    setSavedSummary(null);
  }, []);

  const handleCustomValueChange = useCallback((key, value) => {
    setCustomVitals((prev) =>
      prev.map((v) => (v.key === key ? { ...v, value } : v))
    );
    setSavedSummary(null);
  }, []);

  // Validate a single vital value against its definition
  const getValidation = useCallback((key, value) => {
    if (!value || !value.trim()) return null;
    const defn = VITAL_DEFINITIONS[key];
    if (!defn) return null;
    const num = parseFloat(value);
    if (isNaN(num)) return { type: 'error', message: 'Must be a number' };
    if (num < defn.min || num > defn.max) {
      return {
        type: 'warning',
        message: `Unusual — expected ${defn.min}–${defn.max} ${defn.unit}`,
      };
    }
    return { type: 'ok' };
  }, []);

  const handleSubmit = async () => {
    // Build vitals array
    const vitalsPayload = [];

    // Standard vitals
    for (const key of activeKeys) {
      const val = (values[key] || '').trim();
      if (!val) continue; // skip empty
      const defn = VITAL_DEFINITIONS[key];
      vitalsPayload.push({
        key,
        label: defn.label,
        value: val,
        unit: defn.unit,
        is_custom: false,
      });
    }

    // Custom vitals
    for (const cv of customVitals) {
      if (!cv.value.trim()) continue;
      vitalsPayload.push({
        key: cv.key,
        label: cv.label,
        value: cv.value.trim(),
        unit: cv.unit,
        is_custom: true,
      });
    }

    if (vitalsPayload.length === 0) {
      setError('Please enter at least one vital measurement');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await addVitals(visitId, vitalsPayload);
      setValues({});
      setCustomVitals([]);
      // RV-5 — the summary is exactly what was just WRITTEN (this payload),
      // not a re-fetch: it can never show a stale or eventually-consistent
      // value while the SyncEngine refresh (below, via handleSuccess in the
      // parent) catches up the "Previous Vitals" list.
      setSavedSummary(vitalsPayload);
      onSuccess?.('Vitals recorded successfully');
    } catch (err) {
      setError(err.message || 'Failed to save vitals');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="addvitals-form">
      {/* Header */}
      <div className="av-head">
        <div className="av-title">
          <span className="av-ico" aria-hidden="true">🌡️</span>
          <h3>Add Vitals</h3>
        </div>
        {onClose && (
          <button
            type="button"
            className="av-btn av-btn-icon"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>

      {/* Standard Vitals Grid — DOM order is value-field-first: the
          remove ✕ comes after the input in the DOM (positioned top-right
          visually), so Tab walks every value before any remove control. */}
      <div className="av-grid">
        {activeKeys.map((key) => {
          const defn = VITAL_DEFINITIONS[key];
          if (!defn) return null;
          const val = values[key] || '';
          const validation = getValidation(key, val);
          const isDefault = DEFAULT_VITAL_KEYS.includes(key);
          const stateClass = validation?.type === 'warning' ? 'warn' : validation?.type === 'error' ? 'error' : '';

          return (
            <div key={key} className={`av-card ${stateClass}`}>
              <div className="av-row1">
                <div className="lbl-wrap">
                  <div className="av-lbl">{defn.label}</div>
                  <div className="av-rng">{acceptedRangeLabel(defn)}</div>
                </div>
              </div>
              <div className={`av-field ${stateClass}`}>
                <input
                  type="number"
                  value={val}
                  onChange={(e) => handleValueChange(key, e.target.value)}
                  placeholder={defn.placeholder}
                  min={defn.min}
                  max={defn.max}
                  step={defn.step}
                  aria-label={`${defn.label} in ${defn.unit}`}
                  style={{ minWidth: 0 }}
                />
                <span className="av-unit">{defn.unit}</span>
              </div>
              {validation && validation.type !== 'ok' && (
                <div className={`av-msg ${validation.type}`}>
                  <span aria-hidden="true">⚠</span>
                  <span>{validation.message}</span>
                </div>
              )}
              {!isDefault && (
                <button
                  type="button"
                  onClick={() => handleRemoveVital(key)}
                  className="av-btn av-btn-icon av-btn-danger av-rm"
                  title={`Remove ${defn.label}`}
                  aria-label={`Remove ${defn.label}`}
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}

        {/* Custom vital cards */}
        {customVitals.map((cv) => (
          <div key={cv.key} className="av-card custom">
            <div className="av-row1">
              <div className="lbl-wrap">
                <div className="av-lbl">
                  {cv.label} <span className="av-tag-custom">(custom)</span>
                </div>
                <div className="av-rng">{VITALS_NO_RANGE}</div>
              </div>
            </div>
            <div className="av-field">
              <input
                type="text"
                value={cv.value}
                onChange={(e) => handleCustomValueChange(cv.key, e.target.value)}
                placeholder="Value"
                aria-label={cv.unit ? `${cv.label} in ${cv.unit}` : `${cv.label} value`}
                style={{ minWidth: 0 }}
              />
              {cv.unit && <span className="av-unit">{cv.unit}</span>}
            </div>
            <button
              type="button"
              onClick={() => handleRemoveCustom(cv.key)}
              className="av-btn av-btn-icon av-btn-danger av-rm"
              title={`Remove ${cv.label}`}
              aria-label={`Remove ${cv.label}`}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Add More Vitals */}
      <div className="av-add-wrap">
        <button
          type="button"
          ref={addBtnRef}
          onClick={() => setShowAddMenu((prev) => !prev)}
          className="av-btn"
          aria-haspopup="menu"
          aria-expanded={showAddMenu}
        >
          ＋ Add More Vitals
        </button>

        {/* Dropdown menu — solid panel, closes on Esc + outside click.
            Rect-anchored `fixed` once measured: no ancestor `overflow` can
            clip or shift it (see the comment on `anchor` above). */}
        {showAddMenu && (
          <div
            ref={menuRef}
            className="av-menu"
            role="menu"
            style={placedMenu ? {
              position: 'fixed',
              top: placedMenu.top,
              left: placedMenu.left,
              width: anchor?.width || undefined,
              zIndex: 1000,
            } : undefined}
          >
            {/* Custom Vital is the FIRST row, always visible without
                scrolling — it must never depend on the tail of a long
                standard-vitals list being reachable. */}
            <button
              type="button"
              role="menuitem"
              onClick={() => { setShowCustomForm(true); setShowAddMenu(false); }}
              className="av-item av-custom-item"
            >
              ✏️ Custom Vital...
            </button>
            {availableOptionals.length > 0 && (
              <>
                <div className="av-divider" />
                <div className="av-menu-rows">
                  <div className="av-grp">Standard Vitals</div>
                  {availableOptionals.map((key) => (
                    <button
                      key={key}
                      type="button"
                      role="menuitem"
                      onClick={() => handleAddVital(key)}
                      className="av-item"
                    >
                      {VITAL_DEFINITIONS[key].label}
                      <span className="av-u">({VITAL_DEFINITIONS[key].unit})</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Custom vital form — anchored directly under the trigger */}
      {showCustomForm && (
        <div ref={customFormRef} className="av-custom-form">
          <div className="av-cf-title">Add Custom Vital</div>
          <div className="av-cf-grid">
            <input
              ref={customNameRef}
              type="text"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="Vital name (e.g. BMI)"
              aria-label="Vital name"
              className="av-txt"
            />
            <input
              type="text"
              value={customUnit}
              onChange={(e) => setCustomUnit(e.target.value)}
              placeholder="Unit (optional)"
              aria-label="Unit (optional)"
              className="av-txt"
            />
          </div>
          <div className="av-cf-actions">
            <button
              type="button"
              onClick={handleAddCustom}
              disabled={!customLabel.trim()}
              className="av-btn av-btn-teal"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setShowCustomForm(false); setCustomLabel(''); setCustomUnit(''); }}
              className="av-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* RV-5 — right at the form, immediately after a successful Save: the
          set just written (this render's own `savedSummary` state, not a
          read-back), since the inputs above clear back to placeholders. */}
      {savedSummary && (
        <div className="av-saved" role="status">
          <div className="av-saved-title">✅ Saved</div>
          <div className="av-saved-list">
            {savedSummary.map((v) => (
              <span key={v.key} className="av-saved-chip">
                <span className="av-saved-label">{v.label}:</span>
                <span className="av-saved-value">{v.value}</span>
                <span className="av-saved-unit">{v.unit}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="av-banner" role="alert">⚠️ {error}</div>
      )}

      {/* Actions — full-width, stacked (Save on top) below 460px */}
      <div className="av-footer">
        {onClose && (
          <button type="button" onClick={onClose} className="av-btn">
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="av-btn av-btn-primary"
        >
          {saving ? 'Saving...' : '💾 Save Vitals'}
        </button>
      </div>
    </div>
  );
}
